import React, { useContext, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { cn } from '../../../../lib/utils';
import CreateSlotModal from '../CreateSlotModal/CreateSlotModal';
import Slot from '../../../../types/BotBuilder/Slot';
import ConfirmActionModal from '../../../../components/ConfirmActionModal/ConfirmActionModal';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import SlotValueType, {
  SlotValueTypeLabels
} from '../../../../types/BotBuilder/SlotValueType';
import NotificationSnackbar from '../../../../components/NotificationSnackbar/NotificationSnackbar';

interface SlotToDelete {
  slotId: number | null;
  index: number;
  slotName: string;
}

interface VariablesSettingsProps {
  isLocked?: boolean;
}

function VariablesSettings({
  isLocked = false
}: Readonly<VariablesSettingsProps>): React.ReactElement {
  const { intent, slots, createSlot, updateSlot, deleteSlot } =
    useContext(BotBuilderContext);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingSlot, setEditingSlot] = useState<Slot | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [slotToDelete, setSlotToDelete] = useState<SlotToDelete | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getErrorMessage = (err: any): string => {
    return (
      err?.apiError?.message ??
      err?.message ??
      'Something went wrong. Please try again later.'
    );
  };

  const handleAddSlot = async (values: {
    slotName: string;
    valueType: SlotValueType;
    enumeratedSlotValues: Slot['enumeratedSlotValues'];
  }) => {
    if (!intent) return;
    const enumeratedValues =
      values.valueType === SlotValueType.ENUMERATED_VALUE
        ? values.enumeratedSlotValues
        : [];
    if (editingSlot) {
      const updatedSlot: Slot = {
        ...editingSlot,
        slotName: values.slotName,
        valueType: values.valueType,
        enumeratedSlotValues: enumeratedValues
      };
      await updateSlot(updatedSlot, intent.id!);
    } else {
      const newSlot: Slot = {
        id: null,
        slotName: values.slotName,
        valueType: values.valueType,
        enumeratedSlotValues: enumeratedValues
      };
      await createSlot(newSlot);
    }
    setIsModalOpen(false);
    setEditingSlot(null);
  };

  const confirmDeleteSlot = async () => {
    if (!slotToDelete || !intent) return;
    try {
      await deleteSlot(slotToDelete.slotId!, slotToDelete.index, intent.id!);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setDeleteModalOpen(false);
      setSlotToDelete(null);
    }
  };

  const triggerDelete = (slot: Slot, index: number): void => {
    setSlotToDelete({
      slotId: slot.id,
      index,
      slotName: slot.slotName
    });
    setDeleteModalOpen(true);
  };

  return (
    <div className='flex flex-col'>
      <div className='mb-4 border-b border-white/10 pb-3'>
        <h2 className='mb-2 text-lg font-semibold text-white'>
          Global variables
        </h2>
        <p className='text-sm leading-relaxed text-white/60'>
          Reuse a question by using variables.
          <br />
          You can create conditional responses based on how your user responds.
        </p>
      </div>

      <div className='flex flex-col gap-1.5'>
        {slots.length === 0 ? (
          <p className='py-4 text-center text-sm text-white/60'>
            No variables available
          </p>
        ) : (
          slots.map((slot, index) => (
            <button
              key={slot.id !== null ? `slot-${slot.id}` : `slot-temp-${index}`}
              type='button'
              disabled={isLocked}
              className={cn(
                'flex w-full items-center justify-between rounded-md',
                'bg-white px-3 py-2 text-sm text-black text-left',
                !isLocked
                  ? 'cursor-pointer hover:bg-white/90'
                  : 'cursor-default opacity-60'
              )}
              onClick={(e) => {
                e.stopPropagation();
                setEditingSlot(slot);
                setIsModalOpen(true);
              }}
            >
              <span className='flex-1 truncate font-medium'>
                {slot.slotName}
              </span>
              <div className='ml-2 flex shrink-0 items-center gap-2'>
                <Badge
                  variant='outline'
                  className='border-black/15 bg-black/5 text-xs text-black/70'
                >
                  {SlotValueTypeLabels[slot.valueType]}
                </Badge>
                {!isLocked && (
                  <span
                    role='button'
                    tabIndex={0}
                    aria-label='Delete variable'
                    className='rounded p-1 text-black/50 transition-colors hover:text-black'
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerDelete(slot, index);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        triggerDelete(slot, index);
                      }
                    }}
                  >
                    <X className='h-4 w-4' />
                  </span>
                )}
              </div>
            </button>
          ))
        )}
      </div>

      {!isLocked && (
        <div className='mt-2 flex w-full justify-end pb-6'>
          <button
            type='button'
            onClick={() => {
              setEditingSlot(null);
              setIsModalOpen(true);
            }}
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-md',
              'border border-white/20 bg-transparent px-3 text-sm',
              'text-white/80 hover:bg-white/5 hover:text-white'
            )}
          >
            <Plus size={14} />
            Add variable
          </button>
        </div>
      )}

      <CreateSlotModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingSlot(null);
        }}
        onSubmit={handleAddSlot}
        defaultValues={
          editingSlot
            ? {
                slotName: editingSlot.slotName,
                valueType: editingSlot.valueType,
                enumeratedSlotValues: editingSlot.enumeratedSlotValues
              }
            : undefined
        }
      />
      {slotToDelete && (
        <ConfirmActionModal
          confirmationMessage={`Do you want to delete the global variable '${slotToDelete.slotName}'? This will remove it from the chatbot.`}
          action={confirmDeleteSlot}
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          isDelete
        />
      )}
      {errorMessage && (
        <NotificationSnackbar
          message={errorMessage}
          severity='error'
          onClose={() => setErrorMessage(null)}
        />
      )}
    </div>
  );
}

export default VariablesSettings;
